import { useEffect, useState, type PropsWithChildren, type ReactElement } from "react";
import { useLocation } from "react-router-dom";

import { useLaborActiveSessionContext } from "@/components/LaborActiveSessionProvider";
import { decideLaborLockdown } from "@/components/laborLockdown";
import { ClockedInElsewherePage } from "@/pages/ClockedInElsewherePage";
import {
  LABOR_DEV_SHELL_CHANGED_EVENT,
  LABOR_DEV_SHELL_STORAGE_KEY,
  readLaborDevShellLock,
} from "@/lib/labor/laborDevShellLock";
import { isLaborShellLockdownBypassed, subscribeLaborShellLockdownBypass } from "@/lib/labor/laborEnv";
import { resolveLaborPresence } from "@/lib/labor/laborPresence";
import { getLaborTabId } from "@/lib/labor/laborTabId";

export function LaborLockdownGate({ children }: PropsWithChildren): ReactElement {
  const location = useLocation();
  const { openSession, openPayload } = useLaborActiveSessionContext();
  const [presenceEpoch, setPresenceEpoch] = useState(0);
  const [shellLockdownBypassed, setShellLockdownBypassed] = useState(isLaborShellLockdownBypassed);

  useEffect(() => subscribeLaborShellLockdownBypass(() => setShellLockdownBypassed(isLaborShellLockdownBypassed())), []);

  useEffect(() => {
    const bump = (): void => {
      setPresenceEpoch((value) => value + 1);
    };
    const onDevShellStorage = (event: StorageEvent): void => {
      if (event.key !== null && event.key !== LABOR_DEV_SHELL_STORAGE_KEY) {
        return;
      }
      bump();
    };
    window.addEventListener(LABOR_DEV_SHELL_CHANGED_EVENT, bump);
    window.addEventListener("storage", onDevShellStorage);
    return () => {
      window.removeEventListener(LABOR_DEV_SHELL_CHANGED_EVENT, bump);
      window.removeEventListener("storage", onDevShellStorage);
    };
  }, []);

  const devShell = readLaborDevShellLock();
  void presenceEpoch;

  const presence = resolveLaborPresence({
    openSession,
    devShell,
    activeOnOtherPage: openPayload?.active_on_other_page,
    activeOnOtherSource: openPayload?.active_on_other_source,
  });

  const decision = decideLaborLockdown({
    presence,
    currentTabId: getLaborTabId(),
    currentPathname: location.pathname,
    currentSearch: location.search,
  });

  if (shellLockdownBypassed || presence === null) {
    return <>{children}</>;
  }

  if (decision !== "lockdown") {
    return <>{children}</>;
  }

  return <ClockedInElsewherePage presence={presence} />;
}
